// components/PrintPreviewComponent.js
import React from 'react';
import { baseurl } from '../../url';
import { getFeeCategoryDisplayName } from '../../utils';

export const generatePrintHTML = (studentDetails, feeDetails, photoUrl) => {
  const getTodaysDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  const calculateTotalFee = () => {
    if (!feeDetails) return 0;
    return (
      (parseFloat(feeDetails.admissionFee) || 0) +
      (parseFloat(feeDetails.tuitionFee) || 0) +
      (parseFloat(feeDetails.groupInsuranceandidCard) || 0) +
      (parseFloat(feeDetails.ktuSportsArts) || 0) +
      (parseFloat(feeDetails.ktuAdminFee) || 0) +
      (parseFloat(feeDetails.ktuAffiliationFee) || 0) +
      (parseFloat(feeDetails.cautionDeposit) || 0) +
      (parseFloat(feeDetails.pta) || 0) +
      (parseFloat(feeDetails.busFund) || 0) +
      (parseFloat(feeDetails.trainingPlacement) || 0)
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not Provided";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getAcademicYear = (admissionID) => {
    if (!admissionID) return "Not Available";
    const yearPart = admissionID.split("/")[1];
    let year = parseInt(yearPart, 10);
    if (yearPart.length === 2) {
      year += 2000;
    }
    const nextYear = year + 1;
    return `${year}-${nextYear.toString().slice(-2)}`;
  };

  // Function to get the correct image URL (same as in StudentPreviewModal)
  const getImageUrl = () => {
    // Try to get the photo path from different possible fields
    const photoPath = photoUrl || studentDetails?.photo || studentDetails?.photoUrl || studentDetails?.profilePhoto;
    
    if (!photoPath) return null;
    
    // If it's already a full URL
    if (photoPath.startsWith('http')) {
      return photoPath;
    }
    
    // If it's a base64 string
    if (photoPath.startsWith('data:image')) {
      return photoPath;
    }
    
    // Construct the full URL by joining baseurl with the photo path
    // Remove leading slash if present to avoid double slashes
    const cleanPath = photoPath.startsWith('/') ? photoPath.substring(1) : photoPath;
    return `${baseurl}/${cleanPath}`;
  };

  // IMPORTANT: Check BOTH feeCategory AND feeDetails exists
  const shouldShowFeeDetails = (studentDetails?.feeCategory === "Merit Lower Fee" || 
                                studentDetails?.feeCategory === "Merit Higher Fee") && 
                                feeDetails !== null && feeDetails !== undefined;
  
  const academicYear = getAcademicYear(studentDetails?.admissionId);

  const getCertificateValue = (certificate) => {
    return certificate ? "Yes" : "No";
  };

  const displayFeeCategory = getFeeCategoryDisplayName(studentDetails?.feeCategory);

  const feeTableHTML = `
    <table class="print-table centered-table">
      <tr style="padding-top: 20px; padding-bottom: 20px;">
        <td colspan="2" style="text-align: center; font-weight: bold;">
          ${studentDetails?.course} ${displayFeeCategory} Structure
        </td>
      </tr>
      <tr>
        <td class="first-col">Admission Fee (1st Year Only)</td>
        <td class="second-col">${feeDetails?.admissionFee || 0}</td>
      </tr>
      <tr>
        <td class="first-col">Tuition Fee${shouldShowFeeDetails ? "(per Semester)" : ""}</td>
        <td class="second-col">${feeDetails?.tuitionFee || 0}</td>
      </tr>
      <tr>
        <td class="first-col">Group Insurance Fee and Id Card</td>
        <td class="second-col">${feeDetails?.groupInsuranceandidCard || 0}</td>
      </tr>
      <tr>
        <td class="first-col">KTU Sports and Arts Fee</td>
        <td class="second-col">${feeDetails?.ktuSportsArts || 0}</td>
      </tr>
      <tr>
        <td class="first-col">KTU Administration Fee (1st year only)</td>
        <td class="second-col">${feeDetails?.ktuAdminFee || 0}</td>
      </tr>
      <tr>
        <td class="first-col">KTU Affiliation Fee (1st year only)</td>
        <td class="second-col">${feeDetails?.ktuAffiliationFee || 0}</td>
      </tr>
      <tr>
        <td class="first-col">Caution Deposit (refundable) (1st year only)</td>
        <td class="second-col">${feeDetails?.cautionDeposit || 0}</td>
      </tr>
      <tr>
        <td class="first-col">PTA (One Time)</td>
        <td class="second-col">${feeDetails?.pta || 0}</td>
      </tr>
      <tr>
        <td class="first-col">Bus Fund (One Time)</td>
        <td class="second-col">${feeDetails?.busFund || 0}</td>
      </tr>
      <tr>
        <td class="first-col">Training and Placement Cell (One Time)</td>
        <td class="second-col">${feeDetails?.trainingPlacement || 0}</td>
      </tr>
      <tr>
        <td class="first-col">Total</td>
        <td class="second-col">${calculateTotalFee()}</td>
      </tr>
    </table>
  `;

  const imageUrl = getImageUrl();
  
  // Create a fallback image or empty div if no image
  const photoImageHTML = imageUrl ? 
    `<img src="${imageUrl}" alt="Student Photo" class="photo" width="91" height="129.5" onerror="this.onerror=null; this.src='/images/default-avatar.png'">` :
    `<div class="photo no-photo" style="width: 91px; height: 129.5px; border: 1px solid #ccc; display: flex; align-items: center; justify-content: center; background-color: #f5f5f5;"><span>No Photo</span></div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${studentDetails?.name}'s Details</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css">
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: Calibri, sans-serif;
      font-size: 14pt;
    }
    h1, h2 {
      font-weight: bold;
      text-align: center;
      margin: 5pt 0;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 14pt;
      table-layout: auto;
    }
    td, th {
      border: 1.5pt solid black;
      padding: 10pt;
    }
    .header {
      text-align: center;
      position: relative;
      padding: 10pt 0;
      font-size: 18pt;
      font-weight: bold;
    }
    .logo {
      position: absolute;
      left: 10px;
      top: 10px;
    }
    .photo {
      position: absolute;
      right: 10px;
      top: 10px;
      object-fit: cover;
    }
    .no-photo {
      position: absolute;
      right: 10px;
      top: 10px;
      font-size: 12px;
      text-align: center;
      line-height: 129.5px;
    }
    .header-content {
      margin-top: 30px;
    }
    .header-content p {
      margin: 5pt 0;
    }
    .declaration {
      margin: 10px;
      font-size: 12pt;
    }
    .declaration p {
      margin: 3px 0;
    }
    .declaration .heading {
      text-align: center;
      font-weight: bold;
      font-size: 14pt;
      margin-bottom: 5pt;
    }
    .declaration .content {
      text-align: left;
      font-size: 10pt;
    }
    .signature {
      margin-top: 20px;
      font-size: 11pt;
      display: flex;
      justify-content: space-between;
    }
    .signature .left, .signature .right {
      width: 48%;
    }
    .signature .left {
      text-align: left;
    }
    .signature .right {
      text-align: right;
    }
    .signature .field {
      margin-top: 13px;
    }
    .centered-table {
      width: 80%;
      margin-left: auto;
      margin-right: auto;
      border-collapse: collapse;
      font-size: 12pt;
    }
    .centered-table td {
      padding: 10px;
    }
    .first-col {
      width: 60%;
    }
    .second-col {
      width: 40%;
      text-align: center;
    }
    .cutting-symbol {
      display: inline-block;
      margin-right: 10px;
      vertical-align: middle;
    }
    .cutting-symbol i {
      font-size: 24px;
      color: #000;
    }
    .dotted-line {
      display: inline-block;
      width: 100%;
      border-bottom: 1px dotted black;
      vertical-align: middle;
    }
    @media print {
      .hide-on-print {
        display: none;
      }
      .page-break {
        page-break-before: always;
      }
    }
    @page {
      size: A4;
      margin: 10mm;
    }
  </style>
</head>
<body>
  <!-- Header Section -->
  <div class="header">
    <img src="/images/college__2_-removebg-preview.png" alt="College Logo" class="logo" width="100" height="125">
    ${photoImageHTML}
    <div class="header-content">
      <p>COLLEGE OF ENGINEERING POONJAR</p>
      <p>Managed by IHRD, Govt. of Kerala</p>
      <p>Poonjar Thekkekara P.O. Kottayam Dist. PIN 686 582</p>
      <p>Phone No: 9446122060</p>
      <p>Academic Year: ${academicYear}</p>
    </div>
  </div>

  <table class="print-table">
    <tr>
      <td colspan="2" style="font-weight: bold;">Admission No: ${studentDetails?.admissionNumber}</td>
      <td colspan="2" style="text-align: left;">Submission Date: ${formatDate(studentDetails?.submissionDate)}</td>
    </tr>
    <tr>
      <td>Admission Type</td>
      <td>${studentDetails?.admissionType}</td>
      <td>Allotment Category</td>
      <td>${studentDetails?.allotmentCategory}</td>
    </tr>
    <tr>
      <td>Fee Category</td>
      <td>${displayFeeCategory}</td>
      <td>Course</td>
      <td>${studentDetails?.course}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Student Details</td>
    </tr>
    <tr>
      <td>Name</td>
      <td>${studentDetails?.name}</td>
      <td>Address</td>
      <td>${studentDetails?.address}</td>
    </tr>
    <tr>
      <td>Permanent Address</td>
      <td>${studentDetails?.permanentAddress ?? "Nil"}</td>
      <td>Pin Code</td>
      <td>${studentDetails?.pincode}</td>
    </tr>
    <tr>
      <td>Religion and Community</td>
      <td>${studentDetails?.religion}, ${studentDetails?.community}</td>
      <td>Category</td>
      <td>${studentDetails?.category}</td>
    </tr>
    <tr>
      <td>Gender</td>
      <td>${studentDetails?.gender}</td>
      <td>Date of Birth</td>
      <td>${formatDate(studentDetails?.dateOfBirth)}</td>
    </tr>
    <tr>
      <td>Blood Group</td>
      <td>${studentDetails?.bloodGroup}</td>
      <td>Mobile No</td>
      <td>${studentDetails?.mobileNo}</td>
    </tr>
    <tr>
      <td>WhatsApp No</td>
      <td>${studentDetails?.whatsappNo}</td>
      <td>Email</td>
      <td>${studentDetails?.email}</td>
    </tr>
    <tr>
      <td>Aadhar No</td>
      <td>${studentDetails?.aadharNo}</td>
      <td>Nativity</td>
      <td>${studentDetails?.nativity}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Entrance Exam Details</td>
    </tr>
    <tr>
      <td>Exam Name</td>
      <td>${studentDetails?.entranceExam}</td>
      <td>Roll No</td>
      <td>${studentDetails?.entranceRollNo}</td>
    </tr>
    <tr>
      <td>Rank</td>
      <td>${studentDetails?.entranceRank}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Qualifying Examination Details</td>
    </tr>
    <tr>
      <td>Qualification</td>
      <td>${studentDetails?.qualify?.exam ?? "Nil"}</td>
      <td>Exam Board</td>
      <td>${studentDetails?.qualify?.board}</td>
    </tr>
    <tr>
      <td>Institution Name</td>
      <td>${studentDetails?.qualify?.institution}</td>
      <td>Register No</td>
      <td>${studentDetails?.qualify?.regNo}</td>
    </tr>
    <tr>
      <td>Exam Month and Year</td>
      <td>${studentDetails?.qualify?.examMonthYear}</td>
      <td>Percentage</td>
      <td>${studentDetails?.qualify?.percentage}</td>
    </tr>
    <tr>
      <td>CGPA</td>
      <td>${studentDetails?.qualify?.cgpa ?? "Nil"}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Plus Two Mark Details</td>
    </tr>
    <tr>
      <td>Plus Two Board</td>
      <td>${studentDetails?.marks?.boardType ?? "Nil"}</td>
      <td>Physics</td>
      <td>${studentDetails?.marks?.physics ?? "Nil"}</td>
    </tr>
    <tr>
      <td>Chemistry</td>
      <td>${studentDetails?.marks?.chemistry ?? "Nil"}</td>
      <td>Maths</td>
      <td>${studentDetails?.marks?.maths ?? "Nil"}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Parents Details</td>
    </tr>
    <tr>
      <td>Father's Name</td>
      <td>${studentDetails?.parentDetails?.fatherName ?? "Nil"}</td>
      <td>Mother's Name</td>
      <td>${studentDetails?.parentDetails?.motherName ?? "Nil"}</td>
    </tr>
    <tr>
      <td>Mobile No</td>
      <td>${studentDetails?.parentDetails?.fatherMobileNo ?? "Nil"}</td>
      <td>Mobile No</td>
      <td>${studentDetails?.parentDetails?.motherMobileNo ?? "Nil"}</td>
    </tr>
    <tr>
      <td>Occupation</td>
      <td>${studentDetails?.parentDetails?.fatherOccupation ?? "Nil"}</td>
      <td>Occupation</td>
      <td>${studentDetails?.parentDetails?.motherOccupation ?? "Nil"}</td>
    </tr>
    <tr>
      <td>Annual Income</td>
      <td>${studentDetails?.annualIncome}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Bank Account Details</td>
    </tr>
    <tr>
      <td>Bank Name</td>
      <td>${studentDetails?.bankDetails?.bankName ?? "Nil"}</td>
      <td>Branch</td>
      <td>${studentDetails?.bankDetails?.branch ?? "Nil"}</td>
    </tr>
    <tr>
      <td>Account No</td>
      <td>${studentDetails?.bankDetails?.accountNo ?? "Nil"}</td>
      <td>IFSC Code</td>
      <td>${studentDetails?.bankDetails?.ifscCode ?? "Nil"}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Achievements</td>
    </tr>
    <tr>
      <td>Arts</td>
      <td>${studentDetails?.achievements?.arts ?? "Nil"}</td>
      <td>Sports</td>
      <td>${studentDetails?.achievements?.sports ?? "Nil"}</td>
    </tr>
    <tr>
      <td>Other</td>
      <td colspan="3">${studentDetails?.achievements?.other ?? "Nil"}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align: center; font-weight: bold;">Certificates Provided</td>
    </tr>
    <tr>
      <td>Allotment Memo</td>
      <td>${getCertificateValue(studentDetails?.certificates?.allotmentmemo)}</td>
      <td>10th Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.tenth)}</td>
    </tr>
    <tr>
      <td>12th Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.plusTwo)}</td>
      <td>TC and Conduct Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.tcandconduct)}</td>
    </tr>
    <tr>
      <td>Data Sheet</td>
      <td>${getCertificateValue(studentDetails?.certificates?.Datasheet)}</td>
      <td>Physical Fitness</td>
      <td>${getCertificateValue(studentDetails?.certificates?.physicalfitness)}</td>
    </tr>
    <tr>
      <td>Passportsize Photo (2 Nos)</td>
      <td>${getCertificateValue(studentDetails?.certificates?.passportsizephoto)}</td>
      <td>Income Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.incomecertificates)}</td>
    </tr>
    <tr>
      <td>Community Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.communitycertificate)}</td>
      <td>Caste Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.castecertificate)}</td>
    </tr>
    <tr>
      <td>Copy of Aadhaar Card</td>
      <td>${getCertificateValue(studentDetails?.certificates?.aadhaar)}</td>
      <td>Degree/Diploma Certificate</td>
      <td>${getCertificateValue(studentDetails?.certificates?.degreecertificates)}</td>
    </tr>
    <tr>
      <td>Marklist</td>
      <td>${getCertificateValue(studentDetails?.certificates?.marklist)}</td>
      <td>Other Certificates</td>
      <td>${getCertificateValue(studentDetails?.certificates?.other)}</td>
    </tr>
  </table>

  <div class="declaration">
    <p class="heading">Remarks:</p>
    <br/>
    <p class="content">.............................................................................................................................................................................................................................................................................................................................................................................</p>
    <br/>
    <p class="heading">Declaration of Student:</p>
    <br/>
    <p class="content">
      I hereby undertake on being admitted to the college to abide by the rules and regulations of the college during the course of my study. I will not engage in any undesirable activity either inside or outside the college that will adversely affect orderly working, discipline, and the regulations of the college.
    </p>
    <br/>
    <p><strong>Signature:</strong> ...........................................</p>
    <br/>
    <p class="heading">Declaration of Parent:</p>
    <br/>
    <p class="content">
      I hereby declare that my son/daughter/ward will abide by the rules and regulations of this institution.
    </p>
  </div>

  <div class="signature">
    <div class="left">
      <p><strong>Signature:</strong> ...........................................</p>
    </div>
    <div class="right">
      <p><strong>Place:</strong> Poonjar</p>
      <p><strong>Date:</strong> ${getTodaysDate()}</p>
      <p>Principal</p>
    </div>
  </div>

  <div class="signature">
    <p><strong>Verifying Officer:</strong> ...........................................</p>
    <p><strong>Signature:</strong> ...........................................</p>
  </div>

  ${shouldShowFeeDetails ? `
    <div class="page-break"></div>
    ${feeTableHTML}
    <p class="content" style="text-align: center;">
      Tuition Fee ${feeDetails?.tuitionFee}/- to be remitted in the start of next semester
      <br/>
      Note: Rs 1000/- per year for arts/sports/extracurricular activities to be collected during the odd semester fee collection
    </p>
    <br/><br/><br/><br/>
    <p class="content">
      <span class="cutting-symbol">
        <i class="fas fa-cut"></i>
      </span>
      <span class="dotted-line"></span>
    </p>
    <br/><br/><br/>
    ${feeTableHTML}
    <p class="content" style="text-align: center;">
      Tuition Fee ${feeDetails?.tuitionFee}/- to be remitted in the start of next semester
      <br/>
      Note: Rs 1000/- per year for arts/sports/extracurricular activities to be collected during the odd semester fee collection
    </p>
  ` : ''}

  <button class="hide-on-print" onclick="window.print()">Print</button>
</body>
</html>`;
};